import { useCallback, useEffect, useState } from "react";
import API from "@/lib/api";
import { NETWORK_LABELS } from "@/lib/networkLabels";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import {
  ArrowsClockwise,
  Flag,
  Eye,
  PencilSimple,
  Cookie,
  Globe,
  CheckCircle,
  Prohibit,
} from "@phosphor-icons/react";
import { toast } from "sonner";

const FIELDS = [
  { key: "description", label: "Description", type: "textarea" },
  { key: "cookie_duration", label: "Cookie Duration", type: "input" },
  { key: "allowed_methods", label: "Allowed Methods", type: "list" },
  { key: "payout_groups", label: "Payout Groups", type: "payout-table", cols: ["rank", "condition", "payout"] },
  { key: "payout_restrictions", label: "Payout Restrictions", type: "payout-table", cols: ["condition", "payout"] },
  { key: "creative_guidelines", label: "Creative Guidelines", type: "textarea" },
  { key: "geographic_restrictions", label: "Geographic Restrictions", type: "list" },
  { key: "deeplink_domains", label: "Deeplink Domains", type: "list" },
  { key: "restricted_traffic_sources", label: "Restricted Traffic Sources", type: "list-multiline" },
  { key: "general_requirements", label: "General Requirements", type: "list-multiline" },
  { key: "enforcement_rights", label: "Enforcement Rights", type: "list-multiline" },
];

// Read-only table renderer for Payout Groups / Payout Restrictions
function PayoutTable({ rows, cols }) {
  const arr = Array.isArray(rows) ? rows : [];
  if (arr.length === 0) return <span className="text-slate-400 text-sm">—</span>;
  const labels = { rank: "RANK", condition: "CONDITION", payout: "PAYOUT" };
  return (
    <div className="border border-slate-200 rounded-md overflow-hidden bg-white">
      <table className="w-full text-xs">
        <thead className="bg-slate-50">
          <tr className="border-b border-slate-200">
            {cols.map(c => (
              <th key={c} className={`px-3 py-2 text-left font-bold uppercase tracking-wider text-slate-500 ${c === "rank" ? "w-14" : c === "payout" ? "w-48" : ""}`}>
                {labels[c] || c}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {arr.map((r, i) => (
            <tr key={i} className="border-b border-slate-100 last:border-0">
              {cols.map(c => (
                <td key={c} className={`px-3 py-2 ${c === "rank" ? "font-mono text-slate-500" : c === "payout" ? "font-mono text-[#002FA7]" : "text-slate-800"}`}>
                  {r[c] === "" || r[c] == null ? <span className="text-slate-300">—</span> : String(r[c])}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// Editable table for admin override. Inline cells, Add/Delete row controls.
function PayoutTableEditor({ rows, cols, onChange, placeholder }) {
  const arr = Array.isArray(rows) ? rows : [];
  const labels = { rank: "RANK", condition: "CONDITION", payout: "PAYOUT" };
  const [pasteOpen, setPasteOpen] = useState(false);
  const [pasteText, setPasteText] = useState("");

  const updateCell = (idx, col, value) => {
    const next = [...arr];
    next[idx] = { ...(next[idx] || {}), [col]: value };
    onChange(next);
  };
  const addRow = () => {
    const blank = cols.reduce((a, c) => ({ ...a, [c]: "" }), {});
    onChange([...arr, blank]);
  };
  const removeRow = (idx) => {
    onChange(arr.filter((_, i) => i !== idx));
  };

  // CSV/TSV parser — tolerant to Excel paste (tab-separated) or comma CSV with quoted cells.
  // Skips an optional header row if first cell matches any known column name.
  const parsePaste = (text) => {
    const lines = text.split(/\r?\n/).map(l => l.trim()).filter(Boolean);
    if (lines.length === 0) return [];
    // Auto-detect delimiter: tab if any line has a tab, else comma.
    const delim = lines.some(l => l.includes("\t")) ? "\t" : ",";
    const splitCells = (line) => {
      if (delim === "\t") return line.split("\t").map(c => c.trim());
      // naive CSV: handles quoted fields with commas
      const out = [];
      let cur = "", inQ = false;
      for (let i = 0; i < line.length; i++) {
        const ch = line[i];
        if (ch === '"' && line[i + 1] === '"') { cur += '"'; i++; continue; }
        if (ch === '"') { inQ = !inQ; continue; }
        if (ch === "," && !inQ) { out.push(cur.trim()); cur = ""; continue; }
        cur += ch;
      }
      out.push(cur.trim());
      return out;
    };
    // Skip header row if first cell matches a column label
    const firstCells = splitCells(lines[0]).map(c => c.toLowerCase());
    const headerKnown = ["rank", "condition", "payout"];
    const startIdx = firstCells.some(c => headerKnown.includes(c)) ? 1 : 0;

    const parsed = [];
    for (let i = startIdx; i < lines.length; i++) {
      const cells = splitCells(lines[i]);
      const row = {};
      cols.forEach((col, idx) => {
        const raw = (cells[idx] ?? "").trim();
        if (col === "rank") {
          row[col] = raw === "" ? "" : (Number.isFinite(Number(raw)) ? Number(raw) : raw);
        } else {
          row[col] = raw;
        }
      });
      // Require at least condition or payout to keep the row
      if ((row.condition || "").length > 0 || (row.payout || "").length > 0) {
        parsed.push(row);
      }
    }
    return parsed;
  };

  const applyPaste = (mode) => {
    const parsed = parsePaste(pasteText);
    if (parsed.length === 0) {
      toast.error("No rows detected in pasted text");
      return;
    }
    onChange(mode === "append" ? [...arr, ...parsed] : parsed);
    toast.success(`${mode === "append" ? "Appended" : "Replaced with"} ${parsed.length} row${parsed.length === 1 ? "" : "s"}`);
    setPasteText("");
    setPasteOpen(false);
  };

  return (
    <div className="border border-slate-200 rounded-md overflow-hidden bg-white">
      <table className="w-full text-xs">
        <thead className="bg-slate-50">
          <tr className="border-b border-slate-200">
            {cols.map(c => (
              <th key={c} className={`px-2 py-1.5 text-left font-bold uppercase tracking-wider text-slate-500 ${c === "rank" ? "w-14" : c === "payout" ? "w-56" : ""}`}>
                {labels[c] || c}
              </th>
            ))}
            <th className="px-2 py-1.5 w-8"></th>
          </tr>
        </thead>
        <tbody>
          {arr.length === 0 ? (
            <tr>
              <td colSpan={cols.length + 1} className="px-3 py-4 text-center text-slate-400 text-xs">
                {placeholder || "No rows yet. Click + Add row below."}
              </td>
            </tr>
          ) : arr.map((r, i) => (
            <tr key={i} className="border-b border-slate-100 last:border-0 hover:bg-slate-50">
              {cols.map(c => (
                <td key={c} className="p-0">
                  <input
                    type={c === "rank" ? "number" : "text"}
                    value={r[c] ?? ""}
                    onChange={(e) => updateCell(i, c, c === "rank" ? (e.target.value === "" ? "" : Number(e.target.value)) : e.target.value)}
                    className={`w-full px-2 py-1.5 text-xs border-0 focus:outline-none focus:ring-1 focus:ring-[#002FA7] focus:bg-blue-50 ${c === "rank" ? "font-mono text-slate-700" : c === "payout" ? "font-mono text-[#002FA7]" : "text-slate-800"}`}
                    placeholder={c === "payout" ? "8% of item sale amount" : c === "condition" ? "Item Category contains ..." : ""}
                  />
                </td>
              ))}
              <td className="px-2 text-center">
                <button
                  onClick={() => removeRow(i)}
                  className="text-red-400 hover:text-red-600 text-xs"
                  title="Remove row"
                >
                  ✕
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="border-t border-slate-100 bg-slate-50/50 px-2 py-1.5 flex items-center gap-3">
        <button
          type="button"
          onClick={addRow}
          className="text-[11px] font-semibold text-[#002FA7] hover:text-[#00227A]"
        >
          + Add row
        </button>
        <span className="text-slate-300">|</span>
        <button
          type="button"
          onClick={() => setPasteOpen(!pasteOpen)}
          className="text-[11px] font-semibold text-slate-500 hover:text-slate-700"
          data-testid={`paste-csv-${cols.join("-")}`}
        >
          {pasteOpen ? "× Close paste" : "📋 Paste from spreadsheet"}
        </button>
        <span className="text-[10px] text-slate-400 ml-auto">{arr.length} row{arr.length === 1 ? "" : "s"}</span>
      </div>
      {pasteOpen && (
        <div className="border-t border-slate-200 bg-slate-50/30 p-3 space-y-2">
          <div className="text-[10px] text-slate-500">
            Paste from Excel/Sheets/CSV. Expected columns (in order): <span className="font-mono text-slate-700">{cols.join(" · ")}</span>. Header row optional.
          </div>
          <Textarea
            value={pasteText}
            onChange={(e) => setPasteText(e.target.value)}
            placeholder={
              cols.includes("rank")
                ? "1\tItem Brand contains Apple\t0% of item sale amount\n2\tItem Category contains Gift Cards\tnone\n..."
                : "Item Category contains Gift Cards\tnone\nItem Category contains Subscriptions\tnone\n..."
            }
            rows={6}
            className="text-xs font-mono"
          />
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              className="h-7 text-xs bg-[#002FA7] hover:bg-[#00227A] text-white"
              onClick={() => applyPaste("replace")}
              disabled={!pasteText.trim()}
              data-testid="paste-replace-btn"
            >
              Replace all rows
            </Button>
            <Button
              size="sm"
              variant="outline"
              className="h-7 text-xs"
              onClick={() => applyPaste("append")}
              disabled={!pasteText.trim()}
              data-testid="paste-append-btn"
            >
              Append to existing
            </Button>
            <Button
              size="sm"
              variant="ghost"
              className="h-7 text-xs text-slate-500"
              onClick={() => { setPasteText(""); setPasteOpen(false); }}
            >
              Cancel
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

// Renders structured rule rows: "Item Brand contains Apple".
// Falls back to plain strings for back-compat with older synced data.
function RestrictionRows({ rules }) {
  const arr = Array.isArray(rules) ? rules : [];
  if (arr.length === 0) {
    return <span className="text-slate-400">—</span>;
  }
  return (
    <div className="divide-y divide-slate-200 border border-slate-200 rounded-md overflow-hidden bg-white">
      {arr.map((r, i) => {
        if (typeof r === "string") {
          return (
            <div key={i} className="flex items-center gap-3 px-3 py-2 text-[13px]">
              <span className="w-5 text-[11px] text-slate-400 font-mono">{i + 1}</span>
              <span className="text-slate-700">{r}</span>
            </div>
          );
        }
        return (
          <div key={i} className="flex items-start gap-3 px-3 py-2 text-[13px]">
            <span className="w-5 pt-0.5 text-[11px] text-slate-400 font-mono shrink-0">{i + 1}</span>
            <div className="flex-1 text-slate-700">
              <span className="font-semibold text-slate-900">{r.variable}</span>
              <span className="text-slate-400 mx-1.5">{r.operator}</span>
              <span>{(r.values || []).join(", ")}</span>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// Resolves the "what pub sees" version — override wins per field, else Impact current.
// Mirrors backend services/contracts._effective so preview matches the real pub view.
function resolveEffective(impactCurrent = {}, override = {}) {
  const out = {};
  for (const f of FIELDS) {
    const o = override[f.key];
    const empty = o === undefined || o === null || o === "" || (Array.isArray(o) && o.length === 0);
    out[f.key] = empty ? impactCurrent[f.key] : o;
  }
  // initial_test is a dict field outside the standard FIELDS loop
  const itOverride = override.initial_test || {};
  const itCurrent = impactCurrent.initial_test || {};
  out.initial_test = { ...itCurrent, ...Object.fromEntries(
    Object.entries(itOverride).filter(([, v]) => v !== null && v !== "")
  )};
  // conversion_cap is a scalar field outside the standard FIELDS loop
  const capO = override.conversion_cap;
  out.conversion_cap = (capO === undefined || capO === null || capO === "")
    ? impactCurrent.conversion_cap
    : capO;
  return out;
}

function PublisherPreview({ impactCurrent, override, notes }) {
  const data = resolveEffective(impactCurrent, override);
  const list = (v) => (Array.isArray(v) ? v : []).filter(Boolean);

  return (
    <div
      className="rounded-md bg-slate-50/70 border border-slate-200 p-5 space-y-4 text-sm"
      data-testid="pub-preview-panel"
    >
      <div className="flex items-center gap-2 pb-3 border-b border-slate-200">
        <Eye size={16} className="text-[#002FA7]" />
        <p className="text-[11px] font-bold tracking-[0.3em] uppercase text-slate-500">
          Publisher View Preview
        </p>
        <span className="ml-auto text-[10px] text-slate-400">
          Exactly what pubs see in their Program Details
        </span>
      </div>

      {data.description && (
        <div>
          <div className="text-[10px] font-bold tracking-[0.2em] uppercase text-slate-400 mb-1">
            About
          </div>
          <p className="text-slate-700 leading-relaxed whitespace-pre-wrap">
            {data.description}
          </p>
        </div>
      )}

      <div className="grid md:grid-cols-2 gap-4">
        {data.cookie_duration && (
          <div>
            <div className="text-[10px] font-bold tracking-[0.2em] uppercase text-slate-400 mb-1 flex items-center gap-1">
              <Cookie size={11} /> Cookie Duration
            </div>
            <div className="text-slate-700">{data.cookie_duration}</div>
          </div>
        )}
        {list(data.geographic_restrictions).length > 0 && (
          <div>
            <div className="text-[10px] font-bold tracking-[0.2em] uppercase text-slate-400 mb-1 flex items-center gap-1">
              <Globe size={11} /> Geography
            </div>
            <div className="text-slate-700">
              {list(data.geographic_restrictions).join(", ")}
            </div>
          </div>
        )}
        {(data.conversion_cap ?? "") !== "" && Number(data.conversion_cap) > 0 && (
          <div>
            <div className="text-[10px] font-bold tracking-[0.2em] uppercase text-slate-400 mb-1">
              Conversion Cap
            </div>
            <div className="text-slate-700 font-mono">
              {Number(data.conversion_cap).toLocaleString()} conversions
            </div>
          </div>
        )}
      </div>

      {list(data.allowed_methods).length > 0 && (
        <div>
          <div className="text-[10px] font-bold tracking-[0.2em] uppercase text-slate-400 mb-1 flex items-center gap-1">
            <CheckCircle size={11} /> Allowed Methods
          </div>
          <div className="flex flex-wrap gap-1.5">
            {list(data.allowed_methods).map((m) => (
              <span
                key={m}
                className="inline-block px-2 py-0.5 rounded bg-emerald-50 border border-emerald-200 text-[11px] text-emerald-700"
              >
                {m}
              </span>
            ))}
          </div>
        </div>
      )}

      {(Array.isArray(data.payout_groups) ? data.payout_groups : []).length > 0 && (
        <div>
          <div className="text-[10px] font-bold tracking-[0.2em] uppercase text-slate-400 mb-2">
            Payout Groups
          </div>
          <PayoutTable rows={data.payout_groups} cols={["rank", "condition", "payout"]} />
        </div>
      )}

      {(Array.isArray(data.payout_restrictions) ? data.payout_restrictions : []).length > 0 && (
        <div>
          <div className="text-[10px] font-bold tracking-[0.2em] uppercase text-slate-400 mb-2 flex items-center gap-1">
            <Prohibit size={11} /> Payout Restrictions
          </div>
          <PayoutTable rows={data.payout_restrictions} cols={["condition", "payout"]} />
        </div>
      )}

      {data.creative_guidelines && (
        <div>
          <div className="text-[10px] font-bold tracking-[0.2em] uppercase text-slate-400 mb-1">
            Creative Guidelines
          </div>
          <p className="text-slate-700 whitespace-pre-wrap">{data.creative_guidelines}</p>
        </div>
      )}

      {list(data.deeplink_domains).length > 0 && (
        <div>
          <div className="text-[10px] font-bold tracking-[0.2em] uppercase text-slate-400 mb-1">
            Deeplink Domains
          </div>
          <div className="flex flex-wrap gap-1.5">
            {list(data.deeplink_domains).map((m) => (
              <span
                key={m}
                className="inline-block px-2 py-0.5 rounded bg-slate-100 border border-slate-200 text-[11px] text-slate-600 font-mono"
              >
                {m}
              </span>
            ))}
          </div>
        </div>
      )}

      {(list(data.restricted_traffic_sources).length > 0 ||
        list(data.general_requirements).length > 0 ||
        list(data.enforcement_rights).length > 0) && (
        <div className="border-t border-slate-200 pt-3 space-y-3">
          <p className="text-[10px] font-bold tracking-[0.3em] uppercase text-red-700">
            Restricted Traffic Sources (Not Allowed)
          </p>
          {list(data.restricted_traffic_sources).length > 0 && (
            <ol className="list-decimal ml-5 space-y-1 text-slate-700">
              {list(data.restricted_traffic_sources).map((v, i) => <li key={i}>{v}</li>)}
            </ol>
          )}
          {list(data.general_requirements).length > 0 && (
            <div>
              <p className="text-[10px] font-bold tracking-[0.2em] uppercase text-slate-500 mb-1 mt-2">
                General Requirements
              </p>
              <ol className="list-decimal ml-5 space-y-1 text-slate-700">
                {list(data.general_requirements).map((v, i) => <li key={i}>{v}</li>)}
              </ol>
            </div>
          )}
          {list(data.enforcement_rights).length > 0 && (
            <div>
              <p className="text-[10px] font-bold tracking-[0.2em] uppercase text-slate-500 mb-1 mt-2">
                Enforcement
              </p>
              <p className="text-slate-600 text-[12px] mb-1">
                We actively monitor traffic sources and reserve the right to:
              </p>
              <ol className="list-decimal ml-5 space-y-1 text-slate-700">
                {list(data.enforcement_rights).map((v, i) => <li key={i}>{v}</li>)}
              </ol>
              <p className="text-slate-500 text-[11px] italic mt-2">
                If you have any questions about specific traffic sources or placements, please check with us before launching.
              </p>
            </div>
          )}
        </div>
      )}

      {notes && (
        <div className="border-t border-slate-200 pt-3">
          <div className="text-[10px] font-bold tracking-[0.2em] uppercase text-slate-400 mb-1">
            Notes
          </div>
          <p className="text-slate-700 whitespace-pre-wrap">{notes}</p>
        </div>
      )}

      {data.initial_test && (data.initial_test.clicks_per_day || data.initial_test.duration_days || data.initial_test.min_cr_pct) && (
        <div className="border-t border-slate-200 pt-3">
          <p className="text-[10px] font-bold tracking-[0.3em] uppercase text-amber-700 mb-2">
            Mandatory Initial Test
          </p>
          <div className="grid grid-cols-3 gap-3 text-center mb-2">
            <div className="bg-white border border-slate-200 rounded p-2">
              <div className="text-[9px] uppercase tracking-wider text-slate-400">Clicks / Day</div>
              <div className="text-base font-bold text-slate-900 font-mono">
                {(data.initial_test.clicks_per_day ?? "—").toLocaleString?.() ?? data.initial_test.clicks_per_day ?? "—"}
              </div>
            </div>
            <div className="bg-white border border-slate-200 rounded p-2">
              <div className="text-[9px] uppercase tracking-wider text-slate-400">Duration</div>
              <div className="text-base font-bold text-slate-900 font-mono">
                {data.initial_test.duration_days ?? "—"}<span className="text-[11px] text-slate-500 ml-1">days</span>
              </div>
            </div>
            <div className="bg-white border border-slate-200 rounded p-2">
              <div className="text-[9px] uppercase tracking-wider text-slate-400">Min CR</div>
              <div className="text-base font-bold text-slate-900 font-mono">
                {data.initial_test.min_cr_pct ?? "—"}%
              </div>
            </div>
          </div>
          {data.initial_test.notes && (
            <p className="text-[11px] text-slate-500 italic">{data.initial_test.notes}</p>
          )}
        </div>
      )}

      <div className="border-t border-slate-200 pt-3 flex items-center gap-2 text-[11px] text-slate-400">
        <span className="inline-block h-1.5 w-1.5 rounded-full bg-emerald-500" />
        Pub-safe — no payout % or private contract fields are exposed here.
      </div>
    </div>
  );
}

function ListInput({ value, onChange, placeholder }) {
  const str = Array.isArray(value) ? value.join(", ") : value || "";
  return (
    <Input
      value={str}
      placeholder={placeholder || "Comma-separated values"}
      onChange={(e) =>
        onChange(
          e.target.value
            .split(",")
            .map((s) => s.trim())
            .filter(Boolean)
        )
      }
    />
  );
}

function MultilineListInput({ value, onChange, placeholder }) {
  const str = Array.isArray(value) ? value.join("\n") : value || "";
  return (
    <Textarea
      rows={5}
      value={str}
      placeholder={placeholder || "One item per line"}
      onChange={(e) =>
        onChange(
          e.target.value
            .split("\n")
            .map((s) => s.trim())
            .filter(Boolean)
        )
      }
    />
  );
}

// ─── Contract Screenshot Uploader ───
// Lets admins paste in screenshots from the source network's UI (Impact's
// Payout Groups table, Skimlinks merchant page, etc.) instead of typing
// every detail manually. Pubs see the same thumbnails in their Program
// Details panel and can click to open full-size.
function ContractImagesEditor({ brandId, images, onChange }) {
  const [uploading, setUploading] = useState(false);
  const [lightbox, setLightbox] = useState(null);

  const onFileChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // reset so re-uploading the same file works
    if (!file) return;
    if (file.size > 3 * 1024 * 1024) {
      toast.error(`File too large (${Math.round(file.size / 1024)}KB). Max 3MB.`);
      return;
    }
    const allowed = ["image/png", "image/jpeg", "image/webp", "image/gif", "application/pdf"];
    if (!allowed.includes(file.type)) {
      toast.error("Allowed: PNG, JPG, WEBP, GIF, PDF");
      return;
    }
    setUploading(true);
    try {
      const dataUri = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
      await API.post(`/admin/brands/${brandId}/contract/images`, {
        name: file.name,
        content_type: file.type,
        data: dataUri,
      });
      toast.success(`Uploaded ${file.name}`);
      onChange?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const remove = async (img) => {
    if (!window.confirm(`Delete "${img.name}"?`)) return;
    try {
      await API.delete(`/admin/brands/${brandId}/contract/images/${img.id}`);
      toast.success("Image deleted");
      onChange?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Delete failed");
    }
  };

  return (
    <div className="border border-slate-200 rounded-md p-4 mb-6 bg-slate-50/40" data-testid="contract-images-editor">
      <div className="flex items-center justify-between mb-3">
        <Label className="text-xs uppercase tracking-[0.2em] text-slate-500 font-bold">
          Contract Screenshots
        </Label>
        <span className="text-[10px] text-slate-400">
          Optional · {images.length} / 10 · max 3MB each
        </span>
      </div>
      <p className="text-[11px] text-slate-500 mb-3">
        Drop in screenshots from the source network's UI (Payout Groups table, contract page, etc.) — publishers see them in Program Details.
      </p>

      {images.length > 0 && (
        <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-2 mb-3">
          {images.map((img) => {
            const isPdf = img.content_type === "application/pdf";
            return (
              <div
                key={img.id}
                className="relative group border border-slate-200 rounded-md overflow-hidden bg-white"
              >
                {isPdf ? (
                  <a
                    href={img.data_uri}
                    target="_blank"
                    rel="noreferrer"
                    className="flex flex-col items-center justify-center h-24 bg-rose-50 hover:bg-rose-100 transition-colors"
                  >
                    <span className="text-[10px] font-bold text-rose-700 tracking-wider">PDF</span>
                    <span className="text-[10px] text-rose-600 mt-1 px-2 truncate max-w-full">{img.name}</span>
                  </a>
                ) : (
                  <button
                    type="button"
                    onClick={() => setLightbox(img)}
                    className="block w-full h-24 bg-slate-100 hover:bg-slate-200 transition-colors"
                  >
                    <img
                      src={img.data_uri}
                      alt={img.name}
                      className="w-full h-full object-cover"
                    />
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => remove(img)}
                  className="absolute top-1 right-1 bg-rose-600 text-white text-[10px] w-5 h-5 rounded-full opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center hover:bg-rose-700"
                  data-testid={`contract-img-delete-${img.id}`}
                  title="Delete"
                >
                  ✕
                </button>
                <div className="px-2 py-1 text-[10px] text-slate-600 truncate" title={img.name}>
                  {img.name}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <label
        className={`inline-flex items-center gap-2 px-3 py-2 rounded-md border border-dashed text-xs font-semibold cursor-pointer transition-colors ${
          uploading
            ? "border-slate-200 text-slate-400 cursor-not-allowed"
            : "border-[#002FA7] text-[#002FA7] hover:bg-[#002FA7]/5"
        }`}
        data-testid="contract-img-upload-label"
      >
        <input
          type="file"
          accept="image/png,image/jpeg,image/webp,image/gif,application/pdf"
          onChange={onFileChange}
          disabled={uploading || images.length >= 10}
          className="hidden"
          data-testid="contract-img-upload-input"
        />
        {uploading ? "Uploading…" : images.length >= 10 ? "Limit reached" : "+ Upload screenshot"}
      </label>

      {lightbox && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center bg-black/80 p-4"
          onClick={() => setLightbox(null)}
          data-testid="contract-img-lightbox"
        >
          <img
            src={lightbox.data_uri}
            alt={lightbox.name}
            className="max-w-full max-h-[90vh] object-contain rounded-md shadow-2xl"
          />
        </div>
      )}
    </div>
  );
}

export default function ContractDrawer({ open, onOpenChange, brand, onSaved }) {
  const [contract, setContract] = useState(null);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [override, setOverride] = useState({});
  const [notes, setNotes] = useState("");
  const [preview, setPreview] = useState(false);

  const network = brand?.network || contract?.network || "impact";
  const sourceLabel = NETWORK_LABELS[network] || (network ? network[0].toUpperCase() + network.slice(1) : "feed partner");

  // Networks without a per-brand contract API — the backend will
  // synthesize a baseline contract from platform defaults at sync
  // time. Safe to auto-seed on drawer open since no upstream API
  // call is involved.
  const _NO_API_NETWORKS = new Set([
    "mrge", "cj", "pubadhub", "pubadhub_kevin", "admedia",
    "yieldkit", "shopnomix", "flexoffers", "shopnomix_kevin", "siteplug", "maxbounty", "cashbackhive", "fatcoupon",
    "giddyup", "rga_vishy", "rga_ashok",
  ]);

  const fetchContract = useCallback(async () => {
    if (!brand) return;
    setLoading(true);
    try {
      const { data } = await API.get(`/admin/brands/${brand.campaign_id}/contract`);
      setContract(data);
      setOverride(data.admin_override || {});
      setNotes(data.admin_notes_html || "");
    } catch (err) {
      if (err.response?.status === 404) {
        // Auto-seed default contract for networks without a per-brand
        // contract API so admin always sees a populated editor instead
        // of an empty "Sync from X" prompt.
        const net = brand.network || "";
        if (_NO_API_NETWORKS.has(net)) {
          try {
            await API.post(`/admin/brands/${brand.campaign_id}/sync-contract`);
            const { data } = await API.get(`/admin/brands/${brand.campaign_id}/contract`);
            setContract(data);
            setOverride(data.admin_override || {});
            setNotes(data.admin_notes_html || "");
            return;
          } catch {
            // Fall through to empty state — admin can still click Sync.
          }
        }
        setContract(null);
      }
    } finally {
      setLoading(false);
    }
  }, [brand]);

  useEffect(() => {
    if (open) fetchContract();
  }, [open, fetchContract]);

  const handleSync = async () => {
    setSyncing(true);
    try {
      const { data } = await API.post(`/admin/brands/${brand.campaign_id}/sync-contract`);
      toast.success(
        data.drift
          ? `Re-synced from ${sourceLabel} — changes detected`
          : `Synced from ${sourceLabel}`
      );
      fetchContract();
      onSaved?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to sync");
    } finally {
      setSyncing(false);
    }
  };

  const saveOverride = async () => {
    try {
      await API.put(`/admin/brands/${brand.campaign_id}/contract`, {
        ...override,
        admin_notes_html: notes,
      });
      toast.success("Override saved");
      fetchContract();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    }
  };

  const resetField = async (field) => {
    try {
      await API.delete(`/admin/brands/${brand.campaign_id}/contract/override/${field}`);
      toast.success(`Reset ${field} to ${sourceLabel} version`);
      fetchContract();
    } catch {
      toast.error("Reset failed");
    }
  };

  const toggleApproval = async (value) => {
    try {
      await API.put(`/admin/brands/${brand.campaign_id}/contract/approve`, { approved: value });
      toast.success(value ? "Approved for pub view" : "Approval revoked");
      fetchContract();
      onSaved?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    }
  };

  const impactFor = (field) => contract?.impact_current?.[field];
  const overrideFor = (field) => override?.[field];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl max-h-[92vh] overflow-y-auto" data-testid="contract-drawer">
        <DialogHeader>
          <p className="text-[11px] font-bold tracking-[0.3em] uppercase text-slate-400">
            Program Contract
          </p>
          <DialogTitle className="font-heading text-2xl font-bold tracking-tight">
            {brand?.name}
          </DialogTitle>
        </DialogHeader>

        <div className="flex items-center justify-between gap-3 py-3 border-y border-slate-200 mb-5">
          <div className="flex items-center gap-3">
            <Button size="sm" variant="outline" onClick={handleSync} disabled={syncing} data-testid="sync-contract-btn">
              <ArrowsClockwise size={14} className={`mr-1.5 ${syncing ? "animate-spin" : ""}`} />
              {contract
                ? `Re-sync from ${sourceLabel}`
                : `Sync from ${sourceLabel}`}
            </Button>
            {contract?.last_synced_at && (
              <span className="text-[11px] text-slate-400">
                Last synced: {new Date(contract.last_synced_at).toLocaleString()}
              </span>
            )}
          </div>
          {contract && (
            <div className="flex items-center gap-3">
              <Button
                size="sm"
                variant={preview ? "default" : "outline"}
                onClick={() => setPreview((v) => !v)}
                className={preview ? "bg-[#002FA7] hover:bg-[#00227A] text-white" : ""}
                data-testid="preview-as-pub-btn"
              >
                {preview ? (
                  <>
                    <PencilSimple size={14} className="mr-1.5" /> Back to Edit
                  </>
                ) : (
                  <>
                    <Eye size={14} className="mr-1.5" /> Preview as Publisher
                  </>
                )}
              </Button>
              <Label className="text-xs uppercase tracking-wider text-slate-600 font-bold">
                Approved for publishers
              </Label>
              <Switch
                checked={!!contract.admin_approved}
                onCheckedChange={toggleApproval}
                data-testid="contract-approve-toggle"
              />
            </div>
          )}
        </div>

        {!contract && !loading && (
          <div className="text-center py-16 text-sm text-slate-500">
            {network === "impact" || network === "partnerize" || network === "skimlinks" ? (
              <>No contract synced yet. Click <strong>Sync from {sourceLabel}</strong> to import.</>
            ) : (
              <>
                {sourceLabel} doesn't have a per-brand contract API.{" "}
                <strong>Sync</strong> seeds a baseline contract from our defaults; tune it below and upload reference screenshots.
              </>
            )}
            {brand && (
              <div className="mt-6 max-w-2xl mx-auto text-left">
                <ContractImagesEditor
                  brandId={brand.campaign_id}
                  images={[]}
                  onChange={fetchContract}
                />
              </div>
            )}
          </div>
        )}

        {contract?.drift_detected && (
          <div className="mb-5 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800 flex items-start gap-2" data-testid="drift-banner">
            <Flag size={18} weight="fill" className="shrink-0 mt-0.5" />
            <div>
              <div className="font-semibold">{sourceLabel} changed this contract</div>
              <div className="text-xs mt-0.5">
                Approval was auto-revoked. Review the <em>Original</em> vs <em>Current</em> columns, then re-enable the approval switch.
              </div>
            </div>
          </div>
        )}

        {contract && preview && (
          <PublisherPreview
            impactCurrent={contract.impact_current || {}}
            override={override}
            notes={notes}
          />
        )}

        {contract && !preview && brand && (
          <ContractImagesEditor
            brandId={brand.campaign_id}
            images={contract.contract_images || []}
            onChange={fetchContract}
          />
        )}

        {contract && !preview && (
          <div className="space-y-6">
            {FIELDS.map((f) => {
              const { key, label, type } = f;
              const current = impactFor(key);
              const hasOverride =
                overrideFor(key) !== undefined &&
                overrideFor(key) !== null &&
                overrideFor(key) !== "" &&
                !(Array.isArray(overrideFor(key)) && overrideFor(key).length === 0);
              return (
                <div key={key} className="border border-slate-200 rounded-md p-4">
                  <div className="flex items-center justify-between mb-2">
                    <Label className="text-xs uppercase tracking-[0.2em] text-slate-500 font-bold">
                      {label}
                    </Label>
                    {hasOverride && (
                      <button
                        type="button"
                        onClick={() => resetField(key)}
                        className="text-[11px] text-[#002FA7] hover:underline"
                      >
                        ↺ Reset to {sourceLabel}
                      </button>
                    )}
                  </div>
                  <div className="grid md:grid-cols-2 gap-3">
                    <div>
                      <div className="text-[10px] font-bold tracking-[0.2em] uppercase text-slate-400 mb-1">
                        From {sourceLabel}
                      </div>
                      {type === "rules" ? (
                        <RestrictionRows rules={current} />
                      ) : type === "payout-table" ? (
                        <PayoutTable rows={current} cols={f.cols} />
                      ) : type === "list-multiline" ? (
                        <div className="bg-slate-50 border border-slate-200 rounded-md p-3 text-sm text-slate-700 min-h-[52px]">
                          {Array.isArray(current) && current.length > 0 ? (
                            <ol className="list-decimal ml-5 space-y-1">
                              {current.map((v, i) => <li key={i}>{v}</li>)}
                            </ol>
                          ) : (
                            <span className="text-slate-400">—</span>
                          )}
                        </div>
                      ) : (
                        <div className="bg-slate-50 border border-slate-200 rounded-md p-3 text-sm text-slate-700 min-h-[52px] whitespace-pre-wrap">
                          {Array.isArray(current)
                            ? current.join(", ") || <span className="text-slate-400">—</span>
                            : current || <span className="text-slate-400">—</span>}
                        </div>
                      )}
                    </div>
                    <div>
                      <div className="text-[10px] font-bold tracking-[0.2em] uppercase text-slate-400 mb-1">
                        Your Override {hasOverride ? "(active)" : ""}
                      </div>
                      {type === "textarea" ? (
                        <Textarea
                          rows={3}
                          value={overrideFor(key) ?? ""}
                          placeholder={`Leave blank to use ${sourceLabel} version`}
                          onChange={(e) => setOverride({ ...override, [key]: e.target.value })}
                        />
                      ) : type === "payout-table" ? (
                        <PayoutTableEditor
                          rows={hasOverride ? overrideFor(key) : current}
                          cols={f.cols}
                          onChange={(next) => setOverride({ ...override, [key]: next })}
                          placeholder={`Inherits from ${sourceLabel}. Edit any row to override.`}
                        />
                      ) : type === "list-multiline" ? (
                        <MultilineListInput
                          value={overrideFor(key)}
                          onChange={(v) => setOverride({ ...override, [key]: v })}
                          placeholder="One item per line — blank = use default policy"
                        />
                      ) : type === "list" || type === "rules" ? (
                        <ListInput
                          value={overrideFor(key)}
                          onChange={(v) => setOverride({ ...override, [key]: v })}
                          placeholder={
                            type === "rules"
                              ? `Comma-separated custom restrictions (blank = use ${sourceLabel})`
                              : `Comma-separated (blank = use ${sourceLabel})`
                          }
                        />
                      ) : (
                        <Input
                          value={overrideFor(key) ?? ""}
                          placeholder={`Leave blank to use ${sourceLabel} version`}
                          onChange={(e) => setOverride({ ...override, [key]: e.target.value })}
                        />
                      )}
                    </div>
                  </div>
                </div>
              );
            })}

            {/* Coupon-traffic switch — single toggle. Default OFF for
                the entire catalog; admin flips ON per-brand for the
                merchants who explicitly allow coupon/promo traffic. */}
            <div className="border border-slate-200 rounded-md p-4" data-testid="coupon-traffic-editor">
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <Label className="text-xs uppercase tracking-[0.2em] text-slate-500 font-bold mb-1 block">
                    Coupon traffic allowed
                  </Label>
                  <p className="text-[11px] text-slate-500">
                    OFF by default — coupon / promo-code / cashback traffic blocked across the catalog. Flip ON only for the specific merchants who've confirmed they accept coupon traffic.
                  </p>
                </div>
                <Switch
                  checked={(() => {
                    // Bool field: override wins over current; both default to false.
                    const ov = override.coupon_traffic_allowed;
                    if (typeof ov === "boolean") return ov;
                    return !!contract?.impact_current?.coupon_traffic_allowed;
                  })()}
                  onCheckedChange={(checked) => setOverride({ ...override, coupon_traffic_allowed: checked })}
                  data-testid="coupon-traffic-toggle"
                />
              </div>
            </div>

            {/* Conversion Cap — scalar editor. Feed-sourced (e.g. AdMedia
                <conversion_cap>); admin can override or clear per-brand. */}
            <div className="border border-slate-200 rounded-md p-4" data-testid="conversion-cap-editor">
              {(() => {
                const capCurrent = contract?.impact_current?.conversion_cap;
                const capOverride = override.conversion_cap;
                const hasCapOverride =
                  capOverride !== undefined && capOverride !== null && capOverride !== "";
                return (
                  <>
                    <div className="flex items-center justify-between mb-1">
                      <Label className="text-xs uppercase tracking-[0.2em] text-slate-500 font-bold">
                        Conversion Cap
                      </Label>
                      {hasCapOverride && (
                        <button
                          type="button"
                          onClick={() => resetField("conversion_cap")}
                          className="text-[11px] text-[#002FA7] hover:underline"
                        >
                          ↺ Reset to {sourceLabel}
                        </button>
                      )}
                    </div>
                    <p className="text-[11px] text-slate-500 mb-3">
                      Max conversions the advertiser pays out on (from the {sourceLabel} feed). Leave blank for no cap.
                    </p>
                    <div className="grid md:grid-cols-2 gap-3 items-end">
                      <div>
                        <div className="text-[10px] font-bold tracking-[0.2em] uppercase text-slate-400 mb-1">
                          From {sourceLabel}
                        </div>
                        <div className="bg-slate-50 border border-slate-200 rounded-md p-3 text-sm text-slate-700 font-mono min-h-[40px] flex items-center">
                          {capCurrent ? Number(capCurrent).toLocaleString() : <span className="text-slate-400">No cap</span>}
                        </div>
                      </div>
                      <div>
                        <div className="text-[10px] font-bold tracking-[0.2em] uppercase text-slate-400 mb-1">
                          Your Override {hasCapOverride ? "(active)" : ""}
                        </div>
                        <Input
                          type="number"
                          step="1"
                          min="0"
                          value={capOverride ?? ""}
                          placeholder={`Blank = use ${sourceLabel} value`}
                          onChange={(e) => {
                            const raw = e.target.value;
                            const num = parseInt(raw, 10);
                            setOverride({
                              ...override,
                              conversion_cap: raw === "" ? null : (Number.isFinite(num) ? num : null),
                            });
                          }}
                          data-testid="conversion-cap-input"
                        />
                      </div>
                    </div>
                  </>
                );
              })()}
            </div>

            {/* Mandatory Initial Test — dict editor */}
            <div className="border border-slate-200 rounded-md p-4" data-testid="initial-test-editor">
              <Label className="text-xs uppercase tracking-[0.2em] text-slate-500 font-bold mb-1 block">
                Mandatory Initial Test
              </Label>
              <p className="text-[11px] text-slate-500 mb-3">
                Pre-filled with platform defaults (max 1000 clicks/day, max 7 days, healthy CR 3-4%). Override any field per-brand; leave blank to skip that bound.
              </p>
              {(() => {
                const it = override.initial_test || contract?.impact_current?.initial_test || {};
                const setIt = (patch) =>
                  setOverride({ ...override, initial_test: { ...(override.initial_test || {}), ...patch } });
                // Min/max pairs — both optional. We render Min and Max
                // side-by-side per metric so admin can set a band (e.g.
                // "between 500 and 1000 clicks/day") or just one bound.
                const NumInput = ({ value, placeholder, onChange, step }) => (
                  <Input
                    type="number"
                    step={step}
                    value={value ?? ""}
                    placeholder={placeholder}
                    onChange={(e) => onChange(e.target.value ? Number(e.target.value) : null)}
                    className="h-9 text-sm"
                  />
                );
                return (
                  <div className="space-y-3">
                    <div>
                      <Label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1 block">Clicks / Day</Label>
                      <div className="grid grid-cols-2 gap-2">
                        <NumInput value={it.clicks_per_day_min} placeholder="Min (e.g. 500)" onChange={(v) => setIt({ clicks_per_day_min: v })} />
                        <NumInput value={it.clicks_per_day_max ?? it.clicks_per_day} placeholder="Max (default 500)" onChange={(v) => setIt({ clicks_per_day_max: v, clicks_per_day: null })} />
                      </div>
                    </div>
                    <div>
                      <Label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1 block">Duration (days)</Label>
                      <div className="grid grid-cols-2 gap-2">
                        <NumInput value={it.duration_days_min} placeholder="Min (e.g. 3)" onChange={(v) => setIt({ duration_days_min: v })} />
                        <NumInput value={it.duration_days_max ?? it.duration_days} placeholder="Max (default 7)" onChange={(v) => setIt({ duration_days_max: v, duration_days: null })} />
                      </div>
                    </div>
                    <div>
                      <Label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1 block">Healthy CR % range</Label>
                      <div className="grid grid-cols-2 gap-2">
                        <NumInput step="0.1" value={it.min_cr_pct} placeholder="Min (default 3.0)" onChange={(v) => setIt({ min_cr_pct: v })} />
                        <NumInput step="0.1" value={it.max_cr_pct} placeholder="Max (default 4.0)" onChange={(v) => setIt({ max_cr_pct: v })} />
                      </div>
                      <p className="text-[10px] text-slate-400 mt-1">Below the min, we'd flag the traffic. Above the max, brand may question.</p>
                    </div>
                    <div>
                      <Label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1 block">Notes</Label>
                      <Input
                        value={it.notes ?? ""}
                        placeholder="Scale beyond these limits requires admin approval."
                        onChange={(e) => setIt({ notes: e.target.value })}
                      />
                    </div>
                  </div>
                );
              })()}
            </div>

            <div className="border border-slate-200 rounded-md p-4">
              <Label className="text-xs uppercase tracking-[0.2em] text-slate-500 font-bold mb-2 block">
                Admin Notes (shown to publishers)
              </Label>
              <Textarea
                rows={3}
                value={notes}
                placeholder="Optional — extra context pubs will see"
                onChange={(e) => setNotes(e.target.value)}
              />
            </div>
          </div>
        )}

        <DialogFooter className="mt-6">
          <Button variant="outline" onClick={() => onOpenChange(false)}>Close</Button>
          {contract && !preview && (
            <Button className="bg-[#002FA7] hover:bg-[#00227A] text-white" onClick={saveOverride} data-testid="save-override-btn">
              Save Overrides
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
